import { sizes } from '@lmc/common/constants'
import type { RouterProps } from '@lmc/common/types'
import type { AboutMe, Carer, Home, Resident } from '@lmc/ts-common'
import { find } from 'lodash'
import { type CSSProperties, type FC, useRef } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import {
  connect,
  type MapDispatchToPropsParam,
  type MapStateToPropsParam
} from 'react-redux'
import { AboutMePaywall } from '../../../../components/generic'
import Operations from '../../../../operations'
import { history } from '../../../../store'
import { withRouter } from '../../../../withRouter'
import { AboutMeSection } from './components'

interface StateProps {
  aboutMe: AboutMe
  carers: Carer[]
  features: Home
  printPDF?: boolean
  resident: Resident
  preferredPeopleNominative: string
}

interface DispatchProps {
  saveForm: (form: any, id: string) => Promise<any>
  fetchAboutMe: (id: string) => Promise<any>
  // eslint-disable-next-line react/no-unused-prop-types
  router: RouterProps
}

type Props = StateProps & DispatchProps

export const ResidetAboutMeScreen: FC<Props & { testId?: string }> = ({
  saveForm,
  fetchAboutMe,
  aboutMe,
  carers,
  features,
  printPDF,
  resident,
  testId,
  preferredPeopleNominative
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const styles = {
    container: {
      paddingBottom: 100,
      maxWidth: printPDF ? 'inherit' : sizes.childScreen,
      marginLeft: 'auto',
      marginRight: 'auto'
    }
  }
  const isAboutMeFeatureEnabled = features?.features?.aboutMe?.enabled
  if (!isAboutMeFeatureEnabled) {
    return (
      <AboutMePaywall
        data-testid={testId ? `${testId}-about-me-paywall` : 'about-me-paywall'}
        preferredPeopleNominative={preferredPeopleNominative}
      />
    )
  }

  return (
    <div
      ref={containerRef}
      data-testid={testId ? `${testId}-about-me-screen` : 'about-me-screen'}
    >
      <div
        style={styles.container as CSSProperties}
        data-testid={testId ? `${testId}-about-me-content` : 'about-me-content'}
      >
        <AboutMeSection
          aboutMe={aboutMe}
          carers={carers}
          saveForm={saveForm}
          fetchAboutMe={fetchAboutMe}
          printPDF={printPDF}
          resident={resident}
          containerRef={containerRef}
          onHistoryBack={!isDesktop ? history?.back : undefined}
          testId={testId}
        />
      </div>
    </div>
  )
}

export const mapStateToProps = (
  state: {
    global: {
      home: Home
      user: {
        id: string
      }
    }
    data: {
      'about-me': AboutMe[]
      carers: Carer[]
      residents: Resident[]
      features: Home
    }
  },
  props: { router: { params: any } }
) => {
  const {
    router: {
      params: { id }
    }
  } = props

  return {
    homeId: state.global?.home?.id,
    userId: state.global?.user?.id,
    aboutMe: find(state.data['about-me'], { resident: id }),
    carers: state.data.carers,
    features: state.global.home,
    resident: find(state.data.residents, { id }),
    preferredPeopleNominative: state.global.home.preferredPeopleNominative
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  saveForm: (form: any, id: string) =>
    dispatch(
      Operations.updateListItem({
        listId: 'about-me',
        url: `/list/residents/${id}/about-me`,
        json: form
      })
    ),
  fetchAboutMe: (id: unknown) =>
    dispatch(
      Operations.loadListItem(
        'about-me',
        id,
        null,
        `/api/list/residents/${id}/about-me`
      )
    )
})

export default withRouter(
  connect<StateProps, DispatchProps, Props>(
    // @ts-ignore
    mapStateToProps as MapStateToPropsParam<StateProps, Props, unknown>,
    mapDispatchToProps as unknown as MapDispatchToPropsParam<
      DispatchProps,
      Props
    >
  )(ResidetAboutMeScreen)
)

const isDesktop = isBrowser || isTablet
